/**
 * **영토전 지도**(Todo9 3단계). 삼국의 땅 스물넷을 지도에 놓고, 주인·수도·전선을 보인다. 땅을 누르면 그 땅의 지형과 지키는
 * 병력을 적는다. 남의 땅이면 치러 가기(4단계), 내 땅이면 수비대 두기(5단계 — 병사·수비 대장, 차례마다 군자금).
 *
 * 지도는 SVG 한 장이다 — 반도의 윤곽은 어림한 선이고, 땅은 점, 맞닿은 땅은 선이다. 점을 누르기 쉽게 투명한 큰 원을 겹친다.
 * 키보드로도 고른다(점마다 단추 역할, Enter·Space).
 */
import {
  MAP_H,
  MAP_W,
  TERRITORIES,
  TERRITORY_BY_ID,
  attackableBy,
  garrisonDepth,
  hasUnseenNews,
  isOurNews,
  neighborsOf,
  territoriesOf,
  territoryLinks,
} from '../meta/realm.ts';
import type { RealmState, TerritoryDef } from '../meta/realm.ts';
import type { Army } from '../meta/army.ts';
import { availableCompanions, realmOf, selectionPower, soldierLevel, wageFor } from '../meta/army.ts';
import { canGarrison, deploySoldiers, garrisonCap, garrisonOf, realmIncome, recallGarrison, setCaptain, territoryIncome } from '../meta/garrison.ts';
import { autoPlan, restorePlan } from '../meta/plan.ts';
import { attackPower, defensePower, graceLeft, heldWoundPct, lostDeathPct } from '../meta/campaign.ts';
import { MIN_STAKE, SPECIALTY_GOAL, SPECIALTY_NAMES, specialtyItems, specialtyPreview } from '../meta/specialty.ts';
import { EQUIP_BY_ID } from '../sim/equipment.ts';
import { GRADE_NAMES, GRADE_PCT, SEASON_TURNS, gradeFor, holdingValue, seasonItem, seasonOf, seasonPar, seasonTurn } from '../meta/season.ts';
import type { SeasonRecord } from '../meta/season.ts';
import type { RealmEvent } from '../meta/realm.ts';
import { COMPANIONS, CULTURE_NAMES, Culture, TROOP_BY_ID, boostSpec, depthRoster, foeSubsPower, specForTroop } from '../sim/troops.ts';
import { TRAIT_BY_ID } from '../sim/traits.ts';
import { button, clear, el, objectJosa, subjectJosa } from './dom.ts';

const SVG = 'http://www.w3.org/2000/svg';

/** 나라의 칠. 색만으로 가르지 않게 수도는 겹 고리, 내 나라 땅은 굵은 테두리를 함께 쓴다. */
const CULTURE_CLASS: Record<Culture, string> = {
  [Culture.Goguryeo]: 'realm-go',
  [Culture.Baekje]: 'realm-bj',
  [Culture.Silla]: 'realm-sl',
};

/**
 * 반도와 요동의 어림 윤곽(지도 상자 0–100 × 0–160). 실제 해안선이 아니다 — 땅의 자리를 읽게 하는 바탕이다. 탐라는 따로 그린다.
 */
const OUTLINE =
  'M5,18 L18,8 L36,4 L58,1 L78,3 L94,9 L98,20 L90,27 L84,36 L80,48 L76,60 L72,72 L70,82 L74,92 L80,104 L84,116 ' +
  'L84,126 L78,134 L70,140 L60,142 L50,146 L42,146 L34,142 L30,132 L32,122 L30,112 L28,102 L34,92 L32,86 L30,80 ' +
  'L26,74 L32,68 L30,60 L26,54 L18,56 L8,54 L6,46 L12,40 L8,32 L4,26 Z';

function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
  const n = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
}

/** 그 땅을 지키는 병력(시스템 수비대): 수·레벨·전투력. 주인의 병종, 그 땅의 지형으로. */
export function systemGarrison(realm: RealmState, t: TerritoryDef): { count: number; level: number; power: number } {
  const roster = depthRoster(realm.owner[t.id], garrisonDepth(t.id), [t.land]);
  return {
    count: roster.reduce((n, e) => n + e.count, 0),
    level: roster[0]?.level ?? 1,
    // 부장의 격까지(`foeSubsPower`) — 영토전의 정산(`campaign.defensePower`)과 같은 값이다.
    power:
      roster.reduce((n, e) => n + boostSpec(specForTroop(e.troop, e.level), e.boost).power * e.count, 0) +
      foeSubsPower(realm.owner[t.id], garrisonDepth(t.id), [t.land]),
  };
}

export class RealmScreen {
  private readonly root: HTMLElement;
  private readonly army: Army;
  private readonly onBack: () => void;
  private readonly onAttack: (id: string) => void;
  /** 수비대를 바꿨다 — 계정을 저장한다. */
  private readonly onChange: () => void;
  private selected: string | null = null;
  /** 병사 보내기 목록을 펼쳤나(고른 땅에서). */
  private picking = false;
  /**
   * 칸을 다시 채운 뒤 포커스를 돌려줄 곳(`data-focus`). 칸을 통째로 새로 그리므로 대장을 바꾸거나 목록을 펼치면 누르던
   * 요소가 사라져 키보드 포커스를 잃었다(리뷰가 짚었다).
   */
  private focusKey: string | null = null;
  /** 수입 줄의 자리. 수비대를 바꾸면 다시 채운다 — 처음 그릴 때만 채웠더니 병사를 보내도 줄이 그대로였다. */
  private readonly incomeSlot = el('div', { 'aria-live': 'polite' });

  constructor(root: HTMLElement, army: Army, onBack: () => void, onAttack: (id: string) => void, onChange: () => void) {
    this.root = root;
    this.army = army;
    this.onBack = onBack;
    this.onAttack = onAttack;
    this.onChange = onChange;
  }

  render(): void {
    const realm = realmOf(this.army);
    const mine = this.army.culture;
    const front = new Set(attackableBy(realm, mine).map((t) => t.id));
    clear(this.root);
    this.fillIncome(realm);

    // 자세히 칸은 화면이 서 있는 동안 하나다(`fillDetail`) — 고를 때 내용만 간다.
    const detail = el('section', { class: 'panel realm-detail', 'aria-live': 'polite' });
    this.fillDetail(detail, realm, front);
    const map = svg('svg', { viewBox: `-2 -2 ${MAP_W + 4} ${MAP_H + 4}`, class: 'realm-map', role: 'group', 'aria-label': '삼국 지도' });
    map.append(svg('path', { d: OUTLINE, class: 'realm-land' }), svg('ellipse', { cx: 34, cy: 155, rx: 7, ry: 3.4, class: 'realm-land' }));
    for (const [a, b] of territoryLinks()) {
      const p = TERRITORY_BY_ID.get(a)!;
      const q = TERRITORY_BY_ID.get(b)!;
      const border = realm.owner[a] !== realm.owner[b];
      map.append(svg('line', { x1: p.x, y1: p.y, x2: q.x, y2: q.y, class: border ? 'realm-link border' : 'realm-link' }));
    }
    for (const t of TERRITORIES) {
      const owner = realm.owner[t.id];
      const g = svg('g', {
        class: `realm-node ${CULTURE_CLASS[owner]}${owner === mine ? ' mine' : ''}${front.has(t.id) ? ' front' : ''}${this.selected === t.id ? ' on' : ''}`,
        role: 'button',
        tabindex: 0,
        'aria-pressed': String(this.selected === t.id),
        'aria-label': `${t.name} · ${CULTURE_NAMES[owner]}${t.capital ? ' 수도' : ''}${front.has(t.id) ? ' · 칠 수 있다' : ''}`,
      });
      g.dataset.id = t.id;
      // 누르기 쉬운 큰 투명 원(손가락), 보이는 점, 수도는 겹 고리, 이름.
      g.append(svg('circle', { cx: t.x, cy: t.y, r: 6, class: 'realm-hit' }));
      if (t.capital) g.append(svg('circle', { cx: t.x, cy: t.y, r: 4.6, class: 'realm-capital' }));
      g.append(svg('circle', { cx: t.x, cy: t.y, r: 3, class: 'realm-dot' }));
      // 이름의 자리(`TerritoryDef.label`). 수도는 겹 고리만큼 더 띄운다.
      const gap = t.capital ? 6.2 : 4.6;
      const side = t.label ?? 'n';
      const [lx, ly, anchor] =
        side === 's' ? [t.x, t.y + gap + 2.4, 'middle'] : side === 'e' ? [t.x + gap, t.y + 1.1, 'start'] : side === 'w' ? [t.x - gap, t.y + 1.1, 'end'] : [t.x, t.y - gap, 'middle'];
      const label = svg('text', { x: lx, y: ly, class: 'realm-name', 'text-anchor': anchor });
      label.textContent = t.name;
      g.append(label);
      // 고르면 점의 표시와 자세히 칸만 바꾼다. 처음에는 화면을 통째로 다시 그려, 내용이 찬 `aria-live` 칸이 새로 끼워져
      // 보조기기가 바뀐 것을 읽지 않았다(리뷰가 짚었다). 칸은 그대로 두고 내용만 갈아 낭독되게 한다.
      const pick = (): void => {
        this.selected = t.id;
        this.picking = false;
        for (const n of map.querySelectorAll<SVGGElement>('.realm-node')) {
          const on = n.dataset.id === t.id;
          n.classList.toggle('on', on);
          n.setAttribute('aria-pressed', String(on));
        }
        this.fillDetail(detail, realm, front);
        // 좁은 화면에서는 자세히 칸이 지도 아래라, 고르고 나서 치러 가는 단추가 한참 아래에 있었다(QA). 칸을 보이게 당긴다.
        // 아래 막대(돌아가기)에 가리지 않게 칸의 `scroll-margin-bottom`이 막대 높이만큼 띄운다. 움직임 줄임이면 바로 간다.
        if (window.matchMedia('(max-width: 760px)').matches) {
          const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          detail.scrollIntoView({ block: 'nearest', behavior: calm ? 'auto' : 'smooth' });
        }
      };
      g.addEventListener('click', pick);
      g.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pick();
        }
      });
      map.append(g);
    }

    const legend = el(
      'div',
      { class: 'realm-legend' },
      ...([Culture.Goguryeo, Culture.Baekje, Culture.Silla] as Culture[]).map((c) =>
        el(
          'span',
          { class: `realm-key ${CULTURE_CLASS[c]}${c === mine ? ' mine' : ''}` },
          el('i', { 'aria-hidden': 'true' }),
          `${CULTURE_NAMES[c]} ${territoriesOf(realm, c).length}곳${c === mine ? ' (우리)' : ''}`,
        ),
      ),
    );

    this.root.append(
      el(
        'div',
        { class: 'result-wrap realm-screen' },
        el(
          'header',
          { class: 'result-head' },
          el('span', { class: 'eyebrow' }, `영토전 · ${CULTURE_NAMES[mine]} · 시즌 ${seasonOf(realm)} · ${seasonTurn(realm)}/${SEASON_TURNS}차례`),
          el('h1', {}, '삼국의 땅'),
        ),
        el(
          'section',
          { class: 'panel pick-note' },
          el('p', {}, '우리 땅과 맞닿은 남의 땅을 칠 수 있다(점선 테두리). 수도(겹 고리)는 점령할 수 없다. 수도에 가까운 땅일수록 지키는 병력이 세다.'),
          el('p', { class: 'dim' }, '시제품이다. 남의 땅은 치러 가고, 내 땅에는 병사와 수비 대장을 둔다. 차례마다 각 나라 장수가 우리 땅을 치러 온다 — 잃은 땅이 많은 나라일수록 세게 온다.'),
          this.incomeSlot,
          this.seasonLine(realm),
        ),
        el('div', { class: 'realm-body' }, el('div', { class: 'realm-map-wrap' }, map, legend), detail),
        this.newsPanel(realm),
        ...(realm.history?.length ? [historyPanel(realm.history)] : []),
        // 으뜸 단추로 칠한다. 처음에는 바탕색 단추였는데 막대도 지도도 같은 밝은 회색이라 묻혔다(플레이한 사람이 짚었다:
        // "하단 돌아가기 버튼이 눈에 잘 안띈다"). 이 화면에서 나가는 길은 이것 하나다.
        el('div', { class: 'result-actions' }, button('돌아가기', this.onBack, 'primary')),
      ),
    );
  }

  /**
   * 치러 가기(Todo9 4단계): 출정 화면에서 짜 둔 편성(`restorePlan`, 없으면 자동 편성)으로 나선다. 수비대와 견줄 수 있게
   * 편성의 전투력과 급료를 함께 적는다. 편성이 비었거나 급료가 모자라면 막고 까닭을 적는다.
   */
  private attackBox(t: TerritoryDef, foePower: number): HTMLElement {
    const plan = restorePlan(this.army) ?? autoPlan(this.army);
    const uids = plan.squads.flat();
    const wage = wageFor(this.army, uids);
    const power = selectionPower(this.army, uids);
    const why =
      uids.length === 0
        ? '데려갈 병사가 없다. 출정 화면에서 편성을 짜라.'
        : wage > this.army.gold
          ? `급료 ${wage}냥에 ${wage - this.army.gold}냥이 모자라다.`
          : null;
    const go = button('', () => this.onAttack(t.id), 'danger');
    go.replaceChildren(el('b', {}, `${t.name}${objectJosa(t.name)} 치러 간다`), ' ', el('small', {}, `${uids.length}명 · 급료 ${wage}냥`));
    go.disabled = why !== null;
    return el(
      'div',
      { class: 'realm-attack' },
      el('p', {}, `지금 편성 ${uids.length}명 · 전투력 ${power.toLocaleString()} (수비대 ${foePower.toLocaleString()})`),
      el('p', { class: 'dim' }, '이기면 그 땅을 빼앗는다. 한 판으로 끝나고, 쓰러진 병사는 돌아오지 않는다(이기면 열에 셋쯤은 다쳐서 돌아온다). 편성은 출정 화면에서 바꾼다.'),
      why ? el('p', { class: 'warn' }, why) : null,
      go,
    );
  }

  /** 그 땅의 특산: 무엇을 주는지(이 나라가 입을 수 있는 것), 어떻게 받는지. */
  private specialtyLine(t: TerritoryDef): HTMLElement {
    const items = specialtyItems(t, this.army.culture).map((e) => `${e.name} T${e.tier}`);
    const what = items.length > 0 ? items.join(' · ') : '이 나라가 쓸 수 있는 것이 없다';
    return el('p', { class: 'dim' }, `특산 ${SPECIALTY_NAMES[t.specialty]} — ${what}. 지키면 점수가 쌓여 ${SPECIALTY_GOAL}점마다 하나, 빼앗으면 곧바로 하나.`);
  }

  /**
   * 시즌: 남은 차례, 누적 보유 점수와 눈금, 지금 같으면 받을 등급, 이번 차례에 쌓일 점수. 끝나면 지도가 처음으로 돌아가고
   * 병사·장비·레벨·특산 장부는 남는다.
   */
  private seasonLine(realm: RealmState): HTMLElement {
    const par = seasonPar(this.army.culture);
    const score = realm.seasonScore ?? 0;
    const done = seasonTurn(realm);
    // 지금까지의 속도로 남은 차례를 채우면(가만히 지금 땅을 지키면) 받을 등급.
    const pace = done > 0 ? Math.floor((score * SEASON_TURNS) / done) : holdingValue(realm, this.army.culture) * SEASON_TURNS;
    const grade = gradeFor(pace, par);
    return el(
      'p',
      { class: 'dim' },
      `시즌 ${seasonOf(realm)} — 남은 ${SEASON_TURNS - done}차례 · 누적 보유 점수 ${score.toLocaleString()}(차례마다 쥔 땅의 값, 지금 +${holdingValue(realm, this.army.culture)}) · ` +
        `눈금 ${par.toLocaleString()}(처음 땅을 다 지키면) · 이 속도면 ${GRADE_NAMES[grade]}. 금 ${GRADE_PCT.gold}%·은 ${GRADE_PCT.silver}%·동 ${GRADE_PCT.bronze}%. ` +
        `이번 시즌 금 보상은 ${EQUIP_BY_ID.get(seasonItem(seasonOf(realm)))?.name ?? ''}(시즌 전용). ` +
        '끝나면 지도는 처음으로 돌아가고 병사·장비·레벨·특산 점수는 남는다.',
    );
  }

  /** 영토 소식(각 나라 장수의 공격과 결말), 새것부터. 없으면 안내. */
  private newsPanel(realm: RealmState): HTMLElement {
    const log = [...(realm.log ?? [])].reverse();
    return el(
      'section',
      { class: 'panel realm-news', 'aria-label': '영토 소식' },
      el('h2', {}, '영토 소식'),
      ...(log.length > 0
        ? log.map((e) => newsLine(e))
        : [el('p', { class: 'dim' }, '아직 없다. 출정 한 번이나 영토 전투 한 판마다 한 차례가 돌고, 그때 각 나라 장수가 우리 땅을 치러 올 수 있다.')]),
    );
  }

  /** 수비대의 수입: 지난 차례에 들어온 것과 지금 두면 한 차례에 들어올 것. */
  private fillIncome(realm: RealmState): void {
    clear(this.incomeSlot);
    const now = realmIncome(realm);
    if (now.places > 0 || realm.lastIncome) {
      const last = realm.lastIncome ? `지난 차례 수입 +${realm.lastIncome.gold.toLocaleString()}냥 · ` : '';
      this.incomeSlot.append(
        el('p', { class: 'dim' }, `${last}수비대 ${now.places}곳 · 한 차례에 +${now.total.toLocaleString()}냥(출정 한 번이나 영토 전투 한 판마다)`),
      );
    }
    // 다친 병사(6단계): 병영에도 수비대에도 없다 — 언제 돌아오는지 적는다.
    const inf = realm.infirmary ?? [];
    if (inf.length > 0) {
      const soon = Math.min(...inf.map((x) => x.until)) - realm.turn;
      this.incomeSlot.append(el('p', { class: 'dim' }, `다친 병사 ${inf.length}명 — 가장 먼저 ${Math.max(1, soon)}차례 뒤 병영으로 돌아온다.`));
    }
  }

  /**
   * 내 땅의 수비대(5단계): 대장 고르기, 둔 병사, 병사 보내기(병영에서 고른다), 모두 거두기. 둔 병사는 출정에 나서지 않는다.
   * 바꾸면 계정을 저장하고 칸만 다시 채운다.
   */
  private garrisonBox(t: TerritoryDef, realm: RealmState, refresh: () => void): HTMLElement {
    const g = garrisonOf(realm, t.id);
    const captain = g?.captain ?? null;
    const cap = garrisonCap(this.army, captain);
    const soldiers = g?.soldiers ?? [];
    const changed = (): void => {
      this.onChange();
      this.fillIncome(realm);
      refresh();
    };

    // 대장: 나설 수 있는 동료(요양·다른 땅의 대장 빼고)와 지금 대장.
    const pick = el('select', { 'aria-label': `${t.name} 수비 대장` }) as HTMLSelectElement;
    const options: { id: string; name: string }[] = [];
    if (captain) options.push({ id: captain, name: COMPANIONS.find((c) => c.id === captain)?.name ?? captain });
    for (const d of availableCompanions(this.army)) if (d.id !== captain) options.push({ id: d.id, name: d.name });
    pick.append(el('option', { value: '' }, '대장 없음'), ...options.map((o) => el('option', { value: o.id }, o.name)));
    pick.value = captain ?? '';
    pick.dataset.focus = 'captain';
    pick.addEventListener('change', () => {
      setCaptain(this.army, realm, t.id, pick.value === '' ? null : pick.value);
      this.focusKey = 'captain';
      changed();
    });

    const income = territoryIncome(realm, t.id);
    // 특산 점수(지킨 땅이 쌓는다). 쌓이지 않으면 까닭을 적는다.
    const points = realm.ledger?.[t.id] ?? 0;
    const per = specialtyPreview(realm, t.id);
    const why =
      soldiers.length < MIN_STAKE
        ? `병사가 ${MIN_STAKE}명 이상이어야 쌓인다`
        : per === 0
          ? '둔 뒤 한 차례가 지나야 쌓인다'
          : `다음 차례 +${per}점`;
    const specialtyNote = el('p', { class: 'dim' }, `특산 점수 ${points}/${SPECIALTY_GOAL} — ${why}.`);
    const head = el(
      'p',
      {},
      `수비대 ${soldiers.length}/${cap}명${captain ? '' : ' (대장이 있으면 그 부하장수의 정원까지)'}${income > 0 ? ` · 한 차례 +${income}냥` : ''}`,
    );
    const roster =
      soldiers.length > 0
        ? el('p', { class: 'dim' }, soldiers.map((s) => `${s.name} ${TROOP_BY_ID.get(s.troop)?.name ?? s.troop} Lv${soldierLevel(s)}`).join(' · '))
        : el('p', { class: 'dim' }, '둔 병사가 없다. 병사가 있어야 군자금이 난다.');

    const room = cap - soldiers.length;
    const actions = el('div', { class: 'realm-gar-actions' });
    const send = button(this.picking ? '보내기 접기' : '병사 보내기', () => {
      this.picking = !this.picking;
      // 펼치면 첫 체크 칸으로, 접으면 이 단추로.
      this.focusKey = this.picking ? 'send-first' : 'send';
      refresh();
    }, 'mini');
    send.dataset.focus = 'send';
    send.disabled = room <= 0 || this.army.soldiers.length === 0;
    actions.append(send);
    if (soldiers.length > 0 || captain) {
      actions.append(
        button('모두 거둔다', () => {
          recallGarrison(this.army, realm, t.id);
          this.picking = false;
          this.focusKey = 'captain';
          changed();
        }, 'mini'),
      );
    }

    const box = el(
      'div',
      { class: 'realm-garrison' },
      el('h3', {}, '수비대'),
      el('label', { class: 'realm-captain' }, '수비 대장 ', pick),
      head,
      specialtyNote,
      roster,
      el('p', { class: 'dim' }, '둔 병사와 대장은 출정에 나서지 않는다. 막아 내면 경험치와 상금, 일부는 다친다. 땅을 빼앗기면 그 판에서만 일부 전사하고 나머지는 다쳐서 돌아온다. 부하장수는 죽지 않는다.'),
      actions,
    );
    if (this.picking && room > 0) box.append(this.sendList(t, realm, room, changed));
    return box;
  }

  /** 병사 보내기: 병영의 병사를 골라(정원까지) 이 땅에 둔다. 출정 편성에 있던 병사면 편성에서 빠진다. */
  private sendList(t: TerritoryDef, realm: RealmState, room: number, changed: () => void): HTMLElement {
    const chosen = new Set<number>();
    const go = button('', () => {
      deploySoldiers(this.army, realm, t.id, [...chosen]);
      this.picking = false;
      this.focusKey = 'send';
      changed();
    }, 'primary');
    const sync = (): void => {
      go.textContent = `${chosen.size}명을 보낸다 (남은 자리 ${room})`;
      go.disabled = chosen.size === 0;
    };
    const rows = this.army.soldiers.map((s, k) => {
      const box = el('input', { type: 'checkbox' }) as HTMLInputElement;
      if (k === 0) box.dataset.focus = 'send-first';
      box.addEventListener('change', () => {
        if (box.checked && chosen.size >= room) box.checked = false;
        else if (box.checked) chosen.add(s.uid);
        else chosen.delete(s.uid);
        sync();
      });
      return el('label', { class: 'realm-send-row' }, box, ` ${s.name} · ${TROOP_BY_ID.get(s.troop)?.name ?? s.troop} · Lv${soldierLevel(s)}`);
    });
    sync();
    // 확인 단추는 목록 아래에 붙는다(sticky). 좁은 화면에서 펼치면 목록이 돌아가기 막대 아래로 내려가 단추가 가렸다(QA) —
    // 펼친 목록을 보이게 당긴다(`scroll-margin-bottom`이 막대만큼 띄운다).
    const list = el('div', { class: 'realm-send', role: 'group', 'aria-label': `${t.name}에 보낼 병사` }, ...rows, go);
    requestAnimationFrame(() => {
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      list.scrollIntoView({ block: 'nearest', behavior: calm ? 'auto' : 'smooth' });
    });
    return list;
  }

  /** 고른 땅의 자세히를 칸에 채운다. 고르지 않았으면 안내. 채운 뒤 돌려줄 포커스가 있으면 돌려준다(`focusKey`). */
  private fillDetail(box: HTMLElement, realm: RealmState, front: ReadonlySet<string>): void {
    this.fillDetailBody(box, realm, front);
    const key = this.focusKey;
    this.focusKey = null;
    if (!key) return;
    // 돌려줄 요소가 막혀 있으면(정원을 다 채워 '병사 보내기'가 꺼졌다) 대장 고르기로 — 막힌 단추는 포커스를 받지 못한다.
    const want = box.querySelector(`[data-focus="${key}"]`) as HTMLButtonElement | null;
    const target = want && !want.disabled ? want : (box.querySelector('[data-focus="captain"]') as HTMLElement | null);
    target?.focus({ preventScroll: true });
  }

  private fillDetailBody(box: HTMLElement, realm: RealmState, front: ReadonlySet<string>): void {
    clear(box);
    const t = this.selected ? TERRITORY_BY_ID.get(this.selected) : undefined;
    if (!t) {
      box.append(el('p', { class: 'dim' }, '지도에서 땅을 누르면 자세히 보인다.'));
      return;
    }
    const owner = realm.owner[t.id];
    const mine = this.army.culture;
    const g = systemGarrison(realm, t);
    const status = t.capital
      ? '수도 — 점령할 수 없다.'
      : owner === mine
        ? '우리 땅.'
        : front.has(t.id)
          ? '우리 땅과 맞닿아 칠 수 있다.'
          : '전선에서 멀다 — 맞닿은 땅부터 빼앗아야 한다.';
    const names = neighborsOf(t.id).map((id) => {
      const n = TERRITORY_BY_ID.get(id)!;
      return `${n.name}(${CULTURE_NAMES[realm.owner[id]]})`;
    });
    box.append(
      el('h2', {}, `${t.name} ${t.hanja}`),
      el('p', {}, `${CULTURE_NAMES[owner]} 땅${owner !== t.culture ? ` (본디 ${CULTURE_NAMES[t.culture]})` : ''} · 지형 ${TRAIT_BY_ID.get(t.land)?.name ?? t.land}`),
      el('p', {}, `지키는 병력 ${g.count}명 · 레벨 ${g.level} · 전투력 ${g.power.toLocaleString()}`),
      el('p', { class: owner === mine ? 'good' : front.has(t.id) ? 'warn' : 'dim' }, status),
      el('p', { class: 'dim' }, `맞닿은 땅 — ${names.join(' · ')}`),
      ...(t.capital ? [] : [this.specialtyLine(t)]),
      ...(front.has(t.id) ? [this.attackBox(t, g.power)] : []),
      ...(owner === mine && !t.capital ? [this.threatLine(t, realm)] : []),
      ...(canGarrison(realm, mine, t.id) ? [this.garrisonBox(t, realm, () => this.fillDetail(box, realm, front))] : []),
    );
  }

  /**
   * 우리 땅의 위협(6단계): 맞닿은 나라 장수가 치러 오면 얼마나 센가 — 막는 힘(시스템 수비대 + 우리 수비대)과 견준다. 막 빼앗은
   * 땅이면 유예를 적는다. 각 나라는 차례마다 한 판까지, 잃은 땅이 많을수록 세게·자주 온다.
   */
  private threatLine(t: TerritoryDef, realm: RealmState): HTMLElement {
    const mine = this.army.culture;
    const left = graceLeft(realm, t.id);
    if (left > 0) return el('p', { class: 'dim' }, `막 주인이 바뀐 땅 — 앞으로 ${left}차례 동안은 되찾으러 오지 않는다.`);
    const foes = ([Culture.Goguryeo, Culture.Baekje, Culture.Silla] as Culture[]).filter(
      (c) => c !== mine && neighborsOf(t.id).some((n) => realm.owner[n] === c),
    );
    if (foes.length === 0) return el('p', { class: 'dim' }, '맞닿은 남의 땅이 없다 — 치러 올 나라가 없다.');
    const def = defensePower(this.army, realm, t);
    const worst = Math.max(...foes.map((c) => attackPower(realm, c, t)));
    const who = foes.map((c) => CULTURE_NAMES[c]).join('·');
    // 우리 수비대가 있으면 그 생사도 힘의 비를 따른다(`heldWoundPct`·`lostDeathPct`) — 병사를 더 둘지 거둘지 고르게 적는다.
    const ours = (realm.garrisons?.[t.id]?.soldiers.length ?? 0) > 0;
    return el(
      'p',
      { class: worst > def ? 'warn' : 'dim' },
      `${who}의 장수가 칠 수 있다 — 공격 세기 ${worst.toLocaleString()}, 막는 힘 ${def.toLocaleString()}(이길 몫 ${Math.round((def * 100) / (def + worst))}%).`,
      ours ? ` 막으면 병사마다 ${heldWoundPct(worst, def)}% 다치고, 빼앗기면 ${lostDeathPct(worst, def)}%가 전사한다.` : '',
    );
  }
}

/** 시즌 한 번의 결과 글. 출정 화면 알림과 지도의 기록이 함께 쓴다. */
export function seasonText(h: SeasonRecord): string {
  const pct = h.par > 0 ? Math.floor((h.score * 100) / h.par) : 0;
  const item = h.item ? ` · ${EQUIP_BY_ID.get(h.item)?.name ?? h.item}` : '';
  return `시즌 ${h.season}: ${GRADE_NAMES[h.grade]} — 누적 보유 점수 ${h.score.toLocaleString()}(눈금의 ${pct}%) · +${h.gold.toLocaleString()}냥${item}`;
}

/** 지난 시즌들의 기록(새것부터). */
function historyPanel(history: readonly SeasonRecord[]): HTMLElement {
  return el(
    'section',
    { class: 'panel realm-news', 'aria-label': '시즌 기록' },
    el('h2', {}, '시즌 기록'),
    ...[...history].reverse().map((h) => el('p', { class: h.grade === 'none' ? 'dim' : 'good' }, seasonText(h))),
  );
}

/**
 * 출정 화면 아래쪽의 **영토** 칸: 이번 차례의 소식과 지도로 가는 단추. 화면을 다시 그릴 때마다 다시 짓는다(늘 있다).
 *
 * 처음에는 소식 패널을 출정 화면 **맨 위**에, 차례가 돈 뒤 처음 열 때 한 번만 끼웠다. 폰에서는 그 아래의 머리줄(출정
 * 준비 · 능력치 · 장비)이 패널의 "영토 보기" 단추를 가렸고, 화면을 한 번 고치면(병사를 옮기면) 패널이 사라졌다. 플레이한
 * 사람이 정했다: "영토소식 하단으로 옮기자 … 이제 상단 영토 버튼은 없어도 되겠네." 지도로 가는 길이 이 칸 하나라, 소식이
 * 없어도 칸과 단추는 남는다. 지도를 열어 본 뒤에 난 우리 나라의 일이 있으면 단추에 빨간 점을 단다(`hasUnseenNews`).
 */
export function realmSection(army: Army, onOpen: () => void): HTMLElement {
  const realm = army.realm;
  const lines: HTMLElement[] = [];
  const last = realm?.history?.at(-1);
  if (realm && last && last.season === seasonOf(realm) - 1 && seasonTurn(realm) === 0) {
    // 시즌이 막 끝났으면(새 시즌의 첫 차례 전) 그 결과를 알린다 — 지도가 처음으로 돌아갔다는 것도.
    // 숫자에 조사를 붙이지 않는다("시즌 2이"가 나왔다).
    lines.push(
      el('p', { class: last.grade === 'none' ? 'dim' : 'good' }, `시즌 ${last.season} 끝 — ${seasonText(last)}`),
      el('p', { class: 'dim' }, `지도가 처음으로 돌아가고 새 시즌(시즌 ${seasonOf(realm)})이 열렸다. 수비대·다친 병사는 병영으로 돌아왔다.`),
    );
  } else if (realm) {
    const fresh = (realm.log ?? []).filter((e) => e.turn === realm.turn && concernsUs(realm, army.culture, e));
    for (const e of fresh) lines.push(newsLine(e));
    if (fresh.length === 0) lines.push(el('p', { class: 'dim' }, '이번 차례에는 영토 소식이 없다.'));
  } else {
    lines.push(el('p', { class: 'dim' }, '땅을 지키고 넓힌다. 지도를 열면 영토전이 시작된다.'));
  }
  const unseen = hasUnseenNews(realm);
  const open = button('영토 보기', onOpen, 'mini');
  if (unseen) {
    // 점의 모양은 CSS가 낸다. 뜻은 단추의 이름으로 읽어 준다.
    open.append(el('small', { class: 'nav-badge dot', 'aria-hidden': 'true' }));
    open.setAttribute('aria-label', '영토 보기 — 우리 나라의 새 소식이 있다');
    open.title = '우리 나라의 새 소식이 있다';
  }
  return el('section', { class: 'panel realm-news', 'aria-label': '영토' }, el('div', { class: 'realm-news-head' }, el('h2', {}, '영토'), open), ...lines);
}

/** 소식의 칠: 우리가 잃은 것은 나쁨, 나라끼리의 싸움은 흐림, 나머지(막아 냄·특산)는 좋음. */
export function newsClass(e: RealmEvent): string {
  return e.kind === 'lost' ? 'bad' : e.kind === 'war' || e.kind === 'repel' ? 'dim' : 'good';
}

/** 우리 나라 소식의 딱지 글: 무슨 일인지 한 낱말. */
const OUR_TAGS: Partial<Record<RealmEvent['kind'], string>> = { held: '막아 냄', lost: '빼앗김', gift: '특산' };

/**
 * 소식 한 줄. **우리 나라의 일(`isOurNews` — 막아 냄·빼앗김·특산)은 딱지와 띠로 가른다.** 처음에는 글자색만 달랐다(초록·
 * 빨강 대 흐린 회색) — 나라끼리의 싸움이 차례마다 여러 줄 쌓이면 그 사이의 우리 소식 한 줄이 묻혔다(플레이한 사람이
 * 청했다: "영토소식에 우리나라 관련 소식은 구분이 잘 되게 해줘"). 색만으로 가르지 않는다: 우리 일에는 무슨 일인지 적은
 * 딱지가 붙고, 줄에 띠와 바탕이 깔리고, 남의 일은 작고 흐리다. 출정 화면의 칸·지도의 소식·알림이 함께 쓴다.
 */
export function newsLine(e: RealmEvent): HTMLElement {
  const tag = isOurNews(e) ? OUR_TAGS[e.kind] : undefined;
  if (!tag) return el('p', { class: `news-line ${newsClass(e)}` }, eventText(e));
  return el('p', { class: `news-line ours ${newsClass(e)}` }, el('b', { class: 'news-tag' }, tag), ' ', eventText(e));
}

/**
 * 출정 화면에 알릴 소식인가: 우리 일(막아 냄·빼앗김·특산)이거나, 나라끼리의 싸움이 우리 땅과 맞닿은 땅에서 났을 때. 먼 곳의
 * 싸움까지 알리면 차례마다 알림이 시끄럽다 — 지도의 소식 기록에는 모두 남는다.
 */
export function concernsUs(realm: RealmState, mine: Culture, e: RealmEvent): boolean {
  if (e.kind !== 'war' && e.kind !== 'repel') return true;
  return neighborsOf(e.territory).some((n) => realm.owner[n] === mine);
}

/** 영토 소식 한 건의 글(6단계). 출정 화면의 알림과 지도의 소식이 함께 쓴다. */
export function eventText(e: RealmEvent): string {
  const t = TERRITORY_BY_ID.get(e.territory);
  const place = t?.name ?? e.territory;
  const general = COMPANIONS.find((c) => c.id === e.general)?.name ?? e.general;
  const who = `${CULTURE_NAMES[e.by]}의 ${general}`;
  if (e.kind === 'war' || e.kind === 'repel') {
    // 나라끼리의 싸움.
    const victim = CULTURE_NAMES[e.victim ?? e.by];
    return e.kind === 'war'
      ? `${e.turn}차례: ${who}${subjectJosa(general)} ${victim}의 ${place}${objectJosa(place)} 빼앗았다.`
      : `${e.turn}차례: ${victim}의 ${place}${subjectJosa(place)} ${who}${objectJosa(general)} 막아 냈다.`;
  }
  if (e.kind === 'gift') {
    const item = EQUIP_BY_ID.get(e.item ?? '')?.name ?? e.item ?? '';
    return `${e.turn}차례: ${place}의 특산 — ${item}${objectJosa(item)} 얻었다.`;
  }
  if (e.kind === 'lost') {
    const ours = e.dead + e.hurt > 0 ? ` — 수비대 ${e.dead}명 전사, ${e.hurt}명 부상` : '';
    return `${e.turn}차례: ${who}${subjectJosa(general)} ${place}${objectJosa(place)} 빼앗았다${ours}.`;
  }
  const hurt = e.hurt > 0 ? `, ${e.hurt}명 부상` : '';
  const gain = e.bounty > 0 ? ` — +${e.bounty}냥${hurt}` : hurt ? ` — ${hurt.slice(2)}` : '';
  return `${e.turn}차례: ${place} 수비대가 ${who}${objectJosa(general)} 막아 냈다${gain}.`;
}
